import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import type { TableActionItem } from '@/components/shared';
import { isUuid } from '@/lib/uuid';
import { usePersistedSearchParams } from '@/preferences';
import { fetchBackofficeUsers } from '../api';
import type { BackofficeUserRow, SortDirection, UserSortBy } from '../types';
import { UsersFilters } from './UsersFilters';
import { UsersTable } from './UsersTable';

const DEFAULT_LIMIT = 20;
const SORTABLE_FIELDS = new Set<UserSortBy>(['name', 'lastSeenOnlineAt', 'createdAt']);

export interface UsersListSectionProps {
  fixedOrganizationId?: string;
  showOrganizationFilter?: boolean;
  showOrganizationColumn?: boolean;
  actions?: TableActionItem[];
  preferenceScope?: string;
}

function readSortBy(value: string | null): UserSortBy {
  return value && SORTABLE_FIELDS.has(value as UserSortBy)
    ? (value as UserSortBy)
    : 'lastSeenOnlineAt';
}

function readSortDirection(value: string | null): SortDirection {
  return value === 'asc' || value === 'desc' ? value : 'desc';
}

export function UsersListSection({
  fixedOrganizationId,
  showOrganizationFilter = true,
  showOrganizationColumn = true,
  actions = [],
  preferenceScope = 'list:users',
}: UsersListSectionProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const persistedSearchParamKeys = useMemo(
    () =>
      [
        fixedOrganizationId ? null : 'organizationId',
        'lastSeenFrom',
        'lastSeenTo',
        'createdFrom',
        'createdTo',
        'sortBy',
        'sortDirection',
      ].filter((key): key is string => Boolean(key)),
    [fixedOrganizationId]
  );
  const [users, setUsers] = useState<BackofficeUserRow[]>([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loadMoreError, setLoadMoreError] = useState<string | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const loadingMoreRef = useRef(false);
  const refreshAbortControllerRef = useRef<AbortController | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  const lastSeenFrom = searchParams.get('lastSeenFrom') ?? '';
  const lastSeenTo = searchParams.get('lastSeenTo') ?? '';
  const createdFrom = searchParams.get('createdFrom') ?? '';
  const createdTo = searchParams.get('createdTo') ?? '';
  const organizationId = fixedOrganizationId ?? searchParams.get('organizationId') ?? '';
  const requestOrganizationId = isUuid(organizationId) ? organizationId : '';
  const sortBy = readSortBy(searchParams.get('sortBy'));
  const sortDirection = readSortDirection(searchParams.get('sortDirection'));

  const requestParams = useMemo(() => {
    const params = new URLSearchParams();
    params.set('sortBy', sortBy);
    params.set('sortDirection', sortDirection);
    if (requestOrganizationId) params.set('organizationId', requestOrganizationId);
    if (lastSeenFrom) params.set('lastSeenFrom', lastSeenFrom);
    if (lastSeenTo) params.set('lastSeenTo', lastSeenTo);
    if (createdFrom) params.set('createdFrom', createdFrom);
    if (createdTo) params.set('createdTo', createdTo);
    return params;
  }, [
    createdFrom,
    createdTo,
    lastSeenFrom,
    lastSeenTo,
    requestOrganizationId,
    sortBy,
    sortDirection,
  ]);

  const hasMore = users.length < total;

  const loadUsers = useCallback(
    async (offset: number, signal: AbortSignal) => {
      const params = new URLSearchParams(requestParams);
      params.set('limit', String(DEFAULT_LIMIT));
      params.set('offset', String(offset));

      const response = await fetchBackofficeUsers(params, signal);
      setTotal(response.page.total);
      setUsers((current) => (offset === 0 ? response.users : [...current, ...response.users]));
    },
    [requestParams]
  );

  const handleRefresh = useCallback(async () => {
    refreshAbortControllerRef.current?.abort();
    const controller = new AbortController();
    refreshAbortControllerRef.current = controller;

    setRefreshing(true);
    setError(null);
    setLoadMoreError(null);

    try {
      await loadUsers(0, controller.signal);
    } catch (caught: unknown) {
      if (caught instanceof DOMException && caught.name === 'AbortError') {
        return;
      }
      setUsers([]);
      setTotal(0);
      setError(caught instanceof Error ? caught.message : 'Failed to refresh users');
    } finally {
      if (refreshAbortControllerRef.current === controller) {
        refreshAbortControllerRef.current = null;
      }
      if (!controller.signal.aborted) {
        setRefreshing(false);
      }
    }
  }, [loadUsers]);

  const tableActions = useMemo<TableActionItem[]>(
    () => [
      ...actions,
      {
        id: 'refresh',
        label: 'Refresh',
        loadingLabel: 'Refreshing',
        icon: RefreshCw,
        onSelect: () => {
          void handleRefresh();
        },
        placement: 'fixed-inline',
        disabled: initialLoading,
        isLoading: refreshing,
      },
    ],
    [actions, handleRefresh, initialLoading, refreshing]
  );

  useEffect(() => {
    const controller = new AbortController();
    setInitialLoading(true);
    setError(null);
    setLoadMoreError(null);

    loadUsers(0, controller.signal)
      .catch((caught: unknown) => {
        if (caught instanceof DOMException && caught.name === 'AbortError') {
          return;
        }
        setUsers([]);
        setTotal(0);
        setError(caught instanceof Error ? caught.message : 'Failed to load users');
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setInitialLoading(false);
        }
      });

    return () => controller.abort();
  }, [loadUsers]);

  useEffect(() => {
    return () => refreshAbortControllerRef.current?.abort();
  }, []);

  useEffect(() => {
    const target = loadMoreRef.current;
    if (!target || initialLoading || !hasMore) {
      return;
    }
    if (typeof IntersectionObserver === 'undefined') {
      return;
    }

    const controller = new AbortController();
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) {
          return;
        }
        if (loadingMoreRef.current) {
          return;
        }

        observer.disconnect();
        loadingMoreRef.current = true;
        setLoadingMore(true);
        setLoadMoreError(null);
        loadUsers(users.length, controller.signal)
          .catch((caught: unknown) => {
            if (caught instanceof DOMException && caught.name === 'AbortError') {
              return;
            }
            setLoadMoreError(
              caught instanceof Error ? caught.message : 'Failed to load more users'
            );
          })
          .finally(() => {
            loadingMoreRef.current = false;
            if (!controller.signal.aborted) {
              setLoadingMore(false);
            }
          });
      },
      { rootMargin: '280px' }
    );

    observer.observe(target);

    return () => {
      controller.abort();
      observer.disconnect();
    };
  }, [hasMore, initialLoading, loadUsers, users.length]);

  usePersistedSearchParams({
    scope: preferenceScope,
    keys: persistedSearchParamKeys,
    searchParams,
    setSearchParams,
  });

  function updateParams(updates: Record<string, string | null>) {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      for (const [key, value] of Object.entries(updates)) {
        if (value === null || value === '') {
          next.delete(key);
        } else {
          next.set(key, value);
        }
      }
      return next;
    });
  }

  function handleSortChange(nextSortBy: UserSortBy) {
    const nextDirection = sortBy === nextSortBy && sortDirection === 'asc' ? 'desc' : 'asc';
    updateParams({ sortBy: nextSortBy, sortDirection: nextDirection });
  }

  return (
    <section className="rounded-xl border border-neutral-200 bg-white">
      <UsersFilters
        organizationId={organizationId}
        lastSeenFrom={lastSeenFrom}
        lastSeenTo={lastSeenTo}
        createdFrom={createdFrom}
        createdTo={createdTo}
        actions={tableActions}
        showOrganizationFilter={showOrganizationFilter}
        onOrganizationIdChange={(value) => updateParams({ organizationId: value })}
        onLastSeenRangeChange={(from, to) => updateParams({ lastSeenFrom: from, lastSeenTo: to })}
        onCreatedRangeChange={(from, to) => updateParams({ createdFrom: from, createdTo: to })}
      />

      {initialLoading ? (
        <div className="px-5 py-16 text-center text-body text-neutral-500">Loading users</div>
      ) : error ? (
        <div className="px-5 py-16 text-center text-body text-red-700">{error}</div>
      ) : users.length > 0 ? (
        <>
          <UsersTable
            users={users}
            sortBy={sortBy}
            sortDirection={sortDirection}
            showOrganizationColumn={showOrganizationColumn}
            onSortChange={handleSortChange}
          />
          <div ref={loadMoreRef} className="border-t border-neutral-200 px-5 py-4 text-center">
            {loadingMore ? (
              <span className="text-small text-neutral-500">Loading more users</span>
            ) : loadMoreError ? (
              <span className="text-small text-red-700">{loadMoreError}</span>
            ) : hasMore ? (
              <span className="text-small text-neutral-500">Scroll to load more</span>
            ) : (
              <span className="text-small text-neutral-500">
                Showing {users.length} of {total}
              </span>
            )}
          </div>
        </>
      ) : (
        <div className="px-5 py-16 text-center text-body text-neutral-500">No users found</div>
      )}
    </section>
  );
}
